import {EventEmitter, Injectable} from '@angular/core';


import {catchError, map} from 'rxjs/operators';
import {throwError} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {ApiService} from '../api/api.service';
import {LocalStorage} from '../../utils/storage';
import {User} from '../../classes/user';
import {AuthResponse} from '../../classes/auth-response';
import {Routenames} from '../../routenames';
import {AuthenticationLog} from '../../classes/authenticationlog';
import {Utils} from '../../utils.class';

@Injectable()
export class AuthenticationService {

    static filledInEmail = '';

    private static redirect: string;
    public loggedIn = new EventEmitter<boolean>();
    onLogin = this.loggedIn.asObservable();

    refreshTimeout = null;

    constructor(private apiService: ApiService,
                private matDialog: MatDialog,
                private router: Router) {
    }

    getRedirect() {
        return AuthenticationService.redirect;
    }

    randomPassword(userId: number) {
        return this.apiService.getCall$<{ password: string; }>(`auth/randomPassword/${userId}`);
    }

    setRedirect(redirect) {
        AuthenticationService.redirect = redirect;
    }

    getCurrentUser() {
        return this.apiService.getCall$<User>('auth/me')
            .pipe(map(response => LocalStorage.user = response.data));
    }


    switchToParent() {
        const parentToken = LocalStorage.getParentUserToken();
        LocalStorage.logoutUser();
        LocalStorage.unsetParentUser();
        this.refresh(parentToken).subscribe(() => {
            this.router.navigate([Routenames.Home], {replaceUrl: true});
        }, () => {
            LocalStorage.unsetParentUser();
            this.router.navigate([Routenames.Logout], {replaceUrl: true});
        });
    }

    switchToProfile(token) {
        const parentToken = LocalStorage.userToken;
        const parentUser = LocalStorage.user;
        LocalStorage.logoutUser();
        LocalStorage.setParentUser(parentToken, parentUser);
        return this.refresh(token);
    }

    loginEmail(email) {
        return this.apiService.postCall$<boolean>('auth/login-email', {
            email,
            redirect: AuthenticationService.redirect
        });
    }

    loginCode(email) {
        return this.apiService.postCall$<boolean>('auth/login-code', {
            email,
            redirect: AuthenticationService.redirect
        })
    }

    validateCode(email, pin) {
        return this.apiService.postCall$<AuthResponse>('auth/validate-code', {
            pin,
            email,
            redirect: AuthenticationService.redirect
        }, true)
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    login(email, password) {
        return this.apiService.postCall$<AuthResponse>('auth/login', {
            email,
            password,
            redirect: AuthenticationService.redirect,
            twoFaHash: LocalStorage.twoFaHash
        })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    refresh(token?: string, noErrorDialogs = false) {
        if (token) {
            return this.apiService.postCall$<AuthResponse>('auth/refresh', {token, twoFaHash: LocalStorage.twoFaHash}, noErrorDialogs)
                .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
        }
        return this.apiService.getCall$<AuthResponse>('auth/refresh', {twoFaHash: LocalStorage.twoFaHash}, noErrorDialogs, true)
            .pipe(map(response => this.handleResponse(response.data)));
    }

    refreshTokenIfNeeded() {
        if (LocalStorage.userToken) {
            const token = LocalStorage.userToken;
            const expiryDate = new Date(Utils.getJwtData(token).exp * 1000);
            const now = new Date();
            const refreshAtDate = new Date(expiryDate.getTime());
            refreshAtDate.setUTCMinutes(refreshAtDate.getUTCMinutes() - 5);
            let timeUntilRefresh = refreshAtDate.getTime() - now.getTime();
            if (timeUntilRefresh < 0) { // Als de token al binnen 5 minuten verloopt, refresh dan nu (met random delay)
                timeUntilRefresh = 0;
            }
            const randomDelay = Math.random() * 10 * 1000; // Voeg 0-10 seconden random delay toe om te voorkomen dat meerdere tabs tegelijk refreshen
            clearTimeout(this.refreshTimeout);
            console.log('planned refresh at', refreshAtDate, 'in sec', timeUntilRefresh / 1000);
            this.refreshTimeout = setTimeout(() => {
                if (LocalStorage.userToken === token) {
                    this.refresh().subscribe(() => {

                    }, () => {

                    });
                }
            }, timeUntilRefresh + randomDelay);
        }
    }

    logout() {
        this.matDialog.closeAll();
        return this.apiService.postCall$('auth/logout')
            .pipe(map(response => {
                LocalStorage.logoutUser();
                this.loggedIn.emit(false);
                return response;
            }), catchError(error => {
                LocalStorage.logoutUser();
                this.loggedIn.emit(false);
                return throwError(error);
            }));
    }

    requestRestorePassword(email) {
        return this.apiService.postCall$<AuthResponse>('auth/request-restore', {email});
    }

    verifyResetToken(token: string) {
        return this.apiService.getCall$('auth/verify-reset-token', {token});
    }

    restorePassword(token: string, password: string) {
        return this.apiService.postCall$<AuthResponse>('auth/restore', {password, token})
            .pipe(map(authResponse => this.handleResponse(authResponse.data as AuthResponse)));
    }

    authLog(user_id: number) {
        return this.apiService.getCall$<AuthenticationLog[]>(`auth/log/${user_id}`);
    }

    loginAsUser(userId: number) {
        return this.apiService.postCall$<string>('auth/loginAsUser', {user_id: userId});
    }

    private handleResponse(authResponse: AuthResponse) {
        if (authResponse.redirect) {
            this.setRedirect(authResponse.redirect);
        }
        if (!!authResponse.access_token) {
            LocalStorage.userToken = authResponse.access_token;
            LocalStorage.user = authResponse.user;
            if (authResponse.needsOtp) {
                if (LocalStorage.user.hasOtp) {
                    this.router.navigate([Routenames.Twofactor]);
                } else {
                    this.router.navigateByUrl(`${Routenames.TwofactorSetup}`);
                }
            }
            this.refreshTokenIfNeeded();
            this.loggedIn.emit(true);
            return authResponse;
        } else {
            return null;
        }
    }
}
